import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { peep_client } from '~/src/api/peep';
import Post from '~/src/components/Post/';
import { Post as PostData } from '~/src/types/posts';
import { RefreshCw } from 'lucide-react';
import { useAbort } from '~/src/utils/useAbort';
import { compareDesc } from 'date-fns';
import CreatePostButton from '~/src/components/CreatePostButton';
import Header from '~/src/components/Header';
import { useGroups } from '~src/api_hooks/groups';
import { SWRConfiguration } from '~node_modules/swr/dist/core/index.mjs';
import Modal from '~src/components/Modal';
import JoinGroup from '~src/components/JoinGroup';
import { UuidString } from '~/src/utils/UUID';

export default function Feed() {
  const [posts, setPosts] = useState<null | PostData[]>(null);
  const abortRef = useAbort();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [joinGroupOpen, setJoinGroupOpen] = useState(false);
  const [joinToken, setJoinToken] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get('join_token');
    if (token) {
      setJoinToken(token);
      setJoinGroupOpen(true);
      // Clean up the URL
      searchParams.delete('join_token');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    (async () => {
      //user is not part of a group yet, send them to onboarding
      const groups = await peep_client.groups.getGroupInfo();
      console.log(groups);
      if (groups.length === 0) {
        navigate('/onboarding');
      }

      // If no posts are loaded yet, do the initial load
      if (posts === null) {
        const posts = await peep_client.posts.get_posts(
          { includeRecentComments: true },
          {
            signal: abortRef.current.signal,
          },
        );
        setPosts(posts);
      }
    })().catch(() => {
      console.log('failed to fetch posts, user probably has no groups...loser');
    });
  }, []);

  const handleDeletePost = async (postId: UuidString) => {
    try {
      await peep_client.posts.delete_post(postId);
      setPosts((prevPosts) => (prevPosts ? prevPosts.filter((p) => p.id.toString() !== postId.toString()) : null));
    } catch (error) {
      console.error('Failed to delete post:', error);
    }
  };

  return (
    <div
      className={'Feed'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        backgroundColor: 'var(--global-gray-lighter-7)',
        width: '100%',
      }}
    >
      <Header pageName="Feed" />
      <div
        style={{
          boxSizing: 'border-box',
          flex: 1,
          minHeight: 0,
          marginLeft: '1.5rem',
          marginRight: '1.5rem',
          gap: '1rem',
          display: 'flex',
          flexDirection: 'column',
          paddingTop: '2rem',
          paddingBottom: '2rem',
          paddingRight: '0.5rem',
          paddingLeft: '0.5rem',
          overflow: 'scroll',
          scrollbarWidth: 'none',
        }}
      >
        {posts != null ? <PostsLoaded posts={posts} onDelete={handleDeletePost} /> : <PostsLoading />}
      </div>
      <CreatePostButton />
      {joinGroupOpen && (
        <Modal onClose={() => setJoinGroupOpen(false)}>
          <JoinGroup initialToken={joinToken} />
        </Modal>
      )}
    </div>
  );
}

function NoPosts() {
  return <p>There are no posts in your feed, try creating one!</p>;
}

function PostList({ posts, onDelete }: { posts: PostData[]; onDelete: (id: UuidString) => void }) {
  return posts
    .sort((l, r) => compareDesc(l.posted_at, r.posted_at))
    .map((p) => <Post key={p.id.toString()} initialData={p} onDelete={onDelete} />);
}

function PostsLoaded({ posts, onDelete }: { posts: PostData[]; onDelete: (id: UuidString) => void }) {
  return posts.length > 0 ? <PostList posts={posts} onDelete={onDelete} /> : <NoPosts />;
}

function PostsLoading() {
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    let start: number;
    let more = true;

    const step = (timestamp: number) => {
      if (start === undefined) {
        start = timestamp;
      }

      const elapsed = timestamp - start;
      setRotation((p) => Math.ceil(p + elapsed / 10));

      start = timestamp;
      if (more) {
        window.requestAnimationFrame(step);
      }
    };

    window.requestAnimationFrame(step);

    return () => {
      more = false;
    };
  }, []);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', width: '100%', columnGap: '.5rem', alignItems: 'center' }}>
      <RefreshCw
        style={{
          rotate: `${rotation}deg`,
        }}
      />
      Posts Loading...
    </div>
  );
}
